import type { ReactElement, ReactNode } from "react";
import { useTranslation } from "react-i18next";

import {
  detailLabelClassName,
  detailSecondaryTextClassName,
  detailTertiaryTextClassName,
  detailValueEmphasisClassName,
  detailValueMutedClassName,
  Heading,
} from "@/components/ui/heading";
import { cn } from "@/lib/utils";

/** Bordered nested detail box surface (nested cards). */
export const cellMainSurfaceClassName =
  "min-w-0 rounded-md border border-border bg-muted/40 dark:bg-muted/60";

/** Drawer row — light fill on section card. */
const cellMainRowSurfaceClassName =
  "min-w-0 rounded-md border border-border bg-muted/25 dark:bg-muted/40";

/** Single bordered drawer row. */
export const cellMainClassName = cn(cellMainRowSurfaceClassName, "px-3 py-2.5");

/** Nested box wrapping multiple lines (title, description, meta rows). */
export const cellMainBoxClassName = cn(cellMainSurfaceClassName, "p-4");

const cellMainInlineClassName = "flex flex-wrap items-start gap-x-4 gap-y-1";

const cellMainStackedClassName = "flex flex-col gap-1.5";

const cellMainLabelColumnClassName = cn(
  detailLabelClassName,
  // Size to content; keep one line when the row has room. Value column absorbs squeeze.
  "shrink-0 whitespace-nowrap",
);

/** Grows beside the label; wraps onto its own line when remaining width is too tight. */
const cellMainValueColumnClassName = "min-w-0 flex-1 basis-44 [overflow-wrap:anywhere] text-right";

export type CellMainVariant = "cell" | "plain";

type CellMainProps = {
  label: ReactNode;
  children?: ReactNode;
  /**
   * When set, empty values show `emptyLabel` (defaults to `common.notInformed`) with muted styling;
   * non-empty values use `emphasis` styling automatically. Works for inline and stacked cells.
   */
  value?: string | null;
  emptyLabel?: ReactNode;
  /** When true, info renders below the label when there is a value. Empty `value` stays inline. */
  stacked?: boolean;
  /** De-emphasize the info value (empty / not specified). Ignored when `value` is set. */
  muted?: boolean;
  /** Emphasize the info value. Ignored when `value` is set. */
  emphasis?: boolean;
  /** Timestamp / tertiary metadata — smaller, quieter value text. */
  tertiary?: boolean;
  /** `cell` — bordered row (default). `plain` — no chrome, for grids or nested cards. */
  variant?: CellMainVariant;
  className?: string;
};

function hasCellMainValue(value: string | null | undefined): boolean {
  return Boolean(value?.trim());
}

function cellMainInfoClassName(
  emphasis: boolean,
  muted: boolean,
  tertiary: boolean,
  emptyValue: boolean,
): string {
  if (emptyValue) return "text-sm font-semibold text-muted-foreground/50";
  if (tertiary) return detailTertiaryTextClassName;
  if (muted) return detailValueMutedClassName;
  if (emphasis) return detailValueEmphasisClassName;
  return detailSecondaryTextClassName;
}

function cellMainInfoClassNameBase(emphasis: boolean, muted: boolean, tertiary: boolean): string {
  if (tertiary) return detailTertiaryTextClassName;
  if (muted) return detailValueMutedClassName;
  if (emphasis) return detailValueEmphasisClassName;
  return detailSecondaryTextClassName;
}

export function CellMain({
  label,
  children,
  value,
  emptyLabel,
  stacked = false,
  muted = false,
  emphasis = true,
  tertiary = false,
  variant = "cell",
  className,
}: CellMainProps): ReactElement {
  const { t } = useTranslation();
  const valueDriven = value !== undefined;
  const hasValue = valueDriven ? hasCellMainValue(value) : true;
  const isEmptyValue = valueDriven && !hasValue;
  const resolvedEmphasis = valueDriven ? hasValue && !tertiary : emphasis;
  const resolvedMuted = valueDriven ? !hasValue : muted;
  const resolvedEmptyLabel = emptyLabel ?? (valueDriven ? t("common.notInformed") : undefined);
  const content = valueDriven ? (hasValue ? (children ?? value) : resolvedEmptyLabel) : children;

  const useStackedLayout = stacked && hasValue;

  const infoClassName = cn(
    cellMainInfoClassName(resolvedEmphasis, resolvedMuted, tertiary, isEmptyValue),
    "min-w-0 [overflow-wrap:anywhere]",
  );

  const surfaceClassName = variant === "cell" ? cellMainClassName : "min-w-0 px-0 py-0";

  if (useStackedLayout) {
    return (
      <div className={cn(surfaceClassName, cellMainStackedClassName, className)}>
        <span className={detailLabelClassName}>{label}</span>
        <div className={infoClassName}>{content}</div>
      </div>
    );
  }

  if (variant === "plain") {
    return (
      <p className={cn("text-sm", className)}>
        <span className={detailLabelClassName}>{label}: </span>
        <span
          className={cn(
            cellMainInfoClassNameBase(resolvedEmphasis, resolvedMuted, tertiary),
            "min-w-0 [overflow-wrap:anywhere]",
          )}
        >
          {content}
        </span>
      </p>
    );
  }

  return (
    <div className={cn(surfaceClassName, cellMainInlineClassName, className)}>
      <span className={cellMainLabelColumnClassName}>{label}</span>
      <div className={cn(infoClassName, cellMainValueColumnClassName)}>{content}</div>
    </div>
  );
}

type CellValueProps = {
  children?: ReactNode;
  /**
   * When set, empty values show `emptyLabel` (defaults to `common.notInformed`) with muted styling;
   * non-empty values use `emphasis` styling automatically.
   */
  value?: string | null;
  emptyLabel?: ReactNode;
  /** De-emphasize the value. Ignored when `value` is set. */
  muted?: boolean;
  /** Emphasize the value. Ignored when `value` is set. */
  emphasis?: boolean;
  /** Timestamp / tertiary metadata — smaller, quieter value text. */
  tertiary?: boolean;
  /** `cell` — bordered row (default). `plain` — no chrome, for grids or nested cards. */
  variant?: CellMainVariant;
  className?: string;
};

/** Value-only bordered cell row — same chrome as {@link CellMain}, without a label column. */
export function CellValue({
  children,
  value,
  emptyLabel,
  emphasis = true,
  muted = false,
  tertiary = false,
  variant = "cell",
  className,
}: CellValueProps): ReactElement {
  const { t } = useTranslation();
  const valueDriven = value !== undefined;
  const hasValue = valueDriven ? hasCellMainValue(value) : true;
  const isEmptyValue = valueDriven && !hasValue;
  const resolvedEmphasis = valueDriven ? hasValue && !tertiary : emphasis;
  const resolvedMuted = valueDriven ? !hasValue : muted;
  const resolvedEmptyLabel = emptyLabel ?? (valueDriven ? t("common.notInformed") : undefined);
  const content = valueDriven ? (hasValue ? (children ?? value) : resolvedEmptyLabel) : children;

  const infoClassName = cn(
    cellMainInfoClassName(resolvedEmphasis, resolvedMuted, tertiary, isEmptyValue),
    "min-w-0 [overflow-wrap:anywhere]",
  );

  const surfaceClassName = variant === "cell" ? cellMainClassName : "min-w-0 px-0 py-0";

  if (variant === "plain") {
    return <div className={cn(infoClassName, className)}>{content}</div>;
  }

  return (
    <div className={cn(surfaceClassName, className)}>
      <div className={infoClassName}>{content}</div>
    </div>
  );
}

type CellMainGroupProps = {
  children: ReactNode;
  className?: string;
};

type CellMainItemGroupProps = CellMainGroupProps & {
  /** Item name shown as the group header (e.g. occurrence or service front title). */
  title?: ReactNode;
  titleClassName?: string;
};

/** Vertical stack of bordered {@link CellMain} rows. */
export const cellMainGroupClassName = "flex flex-col gap-1";

export function CellMainGroup({ children, className }: CellMainGroupProps): ReactElement {
  return <div className={cn(cellMainGroupClassName, className)}>{children}</div>;
}

/** Outer frame grouping {@link CellMain} rows that belong to one list item. */
export const cellMainItemGroupClassName =
  "flex flex-col gap-1.5 rounded-xl border border-border p-4";

export function CellMainItemGroup({
  children,
  title,
  titleClassName,
  className,
}: CellMainItemGroupProps): ReactElement {
  return (
    <div className={cn(cellMainItemGroupClassName, className)}>
      {title != null ? (
        <Heading variant="panelStrong" level={4} className={titleClassName}>
          {title}
        </Heading>
      ) : null}
      {children != null ? <div className="flex flex-col gap-1">{children}</div> : null}
    </div>
  );
}

export type CellMainCardField = {
  label: ReactNode;
  /** Empty (nullish, `false`, or blank string) renders `emptyLabel` in muted style. */
  value?: ReactNode;
  /** Render the value below the label instead of inline on the right (for long text). */
  stacked?: boolean;
  valueClassName?: string;
  emptyLabel?: ReactNode;
};

type CellMainCardProps = {
  /** Header shown at the top of the card (e.g. an item name or version). */
  title?: ReactNode;
  /** Node rendered on the right of the title row (e.g. a status chip / tag). */
  titleAside?: ReactNode;
  fields: CellMainCardField[];
  /** Actions/content rendered at the bottom of the card. */
  footer?: ReactNode;
  /** When provided, the whole card becomes an interactive button. */
  onClick?: () => void;
  className?: string;
};

function isEmptyFieldValue(value: ReactNode): boolean {
  if (value == null || value === false) return true;
  if (typeof value === "string") return value.trim() === "";
  return false;
}

/**
 * Light titled card grouping label + value rows in a single drawer-row surface.
 * Inline fields render label-left/value-right; `stacked` fields drop the value below
 * the label; empty values fall back to `common.notInformed`.
 */
export function CellMainCard({
  title,
  titleAside,
  fields,
  footer,
  onClick,
  className,
}: CellMainCardProps): ReactElement {
  const { t } = useTranslation();

  const body = (
    <>
      {title != null || titleAside != null ? (
        <div className="flex flex-wrap items-start justify-between gap-2">
          {title != null ? (
            <span className="text-sm font-semibold text-foreground">{title}</span>
          ) : (
            <span />
          )}
          {titleAside}
        </div>
      ) : null}
      <div className="flex flex-col gap-1.5 text-sm">
        {fields.map((field, index) => {
          const empty = isEmptyFieldValue(field.value);
          const content = empty ? (field.emptyLabel ?? t("common.notInformed")) : field.value;
          const valueClassName = cn(
            empty ? "text-muted-foreground" : "text-foreground",
            field.valueClassName,
          );

          if (field.stacked && !empty) {
            return (
              <div key={index} className="flex flex-col gap-0.5">
                <span className="text-muted-foreground">{field.label}</span>
                <span className={cn("min-w-0 [overflow-wrap:anywhere]", valueClassName)}>{content}</span>
              </div>
            );
          }

          return (
            <div key={index} className="flex flex-wrap items-start gap-x-4 gap-y-1">
              <span className="shrink-0 text-muted-foreground">{field.label}</span>
              <span className={cn(cellMainValueColumnClassName, valueClassName)}>{content}</span>
            </div>
          );
        })}
      </div>
      {footer != null ? <div className="flex gap-2 pt-1">{footer}</div> : null}
    </>
  );

  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        className={cn(
          cellMainClassName,
          "flex w-full cursor-pointer flex-col gap-2 text-left outline-none transition-colors hover:border-border/80 hover:bg-muted/80 focus-visible:ring-3 focus-visible:ring-ring/50",
          className,
        )}
      >
        {body}
      </button>
    );
  }

  return <div className={cn(cellMainClassName, "flex flex-col gap-2", className)}>{body}</div>;
}
